import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SEOHead from '../components/SEOHead';
import { 
  Check, 
  X, 
  Star, 
  ArrowRight, 
  Zap, 
  Shield, 
  Users, 
  Database,
  Globe,
  Clock,
  MessageSquare,
  Phone,
  Mail,
  Calendar,
  TrendingUp,
  Award,
  Building2,
  Settings,
  BarChart3,
  Workflow,
  Cloud,
  Lock,
  RefreshCw,
  Eye,
  Bell,
  FileText,
  PieChart,
  LineChart,
  Activity,
  Crown,
  Rocket,
  Target,
  Heart
} from 'lucide-react';

const Pricing: React.FC = () => {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [isContactOpen, setIsContactOpen] = useState(false);
  const openContact = () => setIsContactOpen(true);
  const closeContact = () => setIsContactOpen(false);

  const plans = [
    {
      id: 'basic',
      name: 'Basic Development & Support',
      icon: Rocket,
      color: 'from-blue-500 to-cyan-500',
      monthlyPriceMin: 199,
      monthlyPriceMax: 299,
      yearlyPriceMin: 1990,
      yearlyPriceMax: 2990,
      description: 'Perfect for small clients getting started with Power Platform solutions.',
      features: [
        'Canvas App Creation',
        'Low-code Automation',
        'Minor Dataverse Customization',
        'Email Support',
        '1-2 Hours/Month Support',
        'Basic Training'
      ],
      notIncluded: [
        'Power Automate Flows',
        'Power BI Dashboards',
        'Model-driven Apps',
        'Custom Connectors',
        'Priority Support'
      ],
      popular: false
    },
    {
      id: 'professional',
      name: 'Professional Development & Support',
      icon: Target,
      color: 'from-purple-500 to-pink-500',
      monthlyPriceMin: 499,
      monthlyPriceMax: 999,
      yearlyPriceMin: 4990,
      yearlyPriceMax: 9990,
      description: 'Ideal for growing businesses that need advanced Power Platform features.',
      features: [
        'Power Automate Flows',
        'Power BI Dashboards',
        'Model-driven Apps',
        'Custom Connectors',
        'Priority Ticket Support',
        '5-10 Hours/Month Support',
        'Advanced Training',
        'API Integration'
      ],
      notIncluded: [
        'Full Power Platform Suite',
        'Premium Connectors',
        'Complex Integrations',
        'Dedicated Manager'
      ],
      popular: true
    },
    {
      id: 'enterprise',
      name: 'Enterprise-Level Development & Consulting',
      icon: Crown,
      color: 'from-orange-500 to-red-500',
      monthlyPriceMin: 1499,
      monthlyPriceMax: 2499,
      yearlyPriceMin: 14990,
      yearlyPriceMax: 24990,
      description: 'Complete Power Platform solution for large enterprises with complex requirements.',
      features: [
        'Full Power Platform Suite',
        'Power Apps, Automate, BI, Virtual Agents',
        'Premium Connectors',
        'Complex Integrations',
        'Governance Setup',
        'Dedicated Manager',
        'Strategic Consulting',
        'Training Sessions',
        'Unlimited Tickets',
        'Project-based Options'
      ],
      notIncluded: [],
      popular: false
    }
  ];

  const addOns = [
    {
      name: 'Custom Power Apps Development',
      description: 'Tailored Power Apps solutions built specifically for your business needs.',
      price: 200,
      icon: Settings
    },
    {
      name: 'Power Platform Training',
      description: 'Expert guidance to maximize your Power Platform investment.',
      price: 250,
      icon: Users
    },
    {
      name: 'Data Migration & Integration',
      description: 'Seamless migration and integration with your existing systems.',
      price: 150,
      icon: Database
    },
    {
      name: 'Premium Support Hours',
      description: 'Additional support hours for critical business operations.',
      price: 100,
      icon: Phone
    }
  ];

  const features = [
    {
      category: 'Power Platform Development',
      items: [
        { name: 'Canvas App Creation', basic: true, professional: true, enterprise: true },
        { name: 'Low-code Automation', basic: true, professional: true, enterprise: true },
        { name: 'Dataverse Customization', basic: true, professional: true, enterprise: true },
        { name: 'Power Automate Flows', basic: false, professional: true, enterprise: true },
        { name: 'Power BI Dashboards', basic: false, professional: true, enterprise: true },
        { name: 'Model-driven Apps', basic: false, professional: true, enterprise: true },
        { name: 'Custom Connectors', basic: false, professional: true, enterprise: true },
        { name: 'Premium Connectors', basic: false, professional: false, enterprise: true },
        { name: 'Virtual Agents', basic: false, professional: false, enterprise: true },
        { name: 'Complex Integrations', basic: false, professional: false, enterprise: true }
      ]
    },
    {
      category: 'Support & Services',
      items: [
        { name: 'Email Support', basic: true, professional: true, enterprise: true },
        { name: 'Priority Ticket Support', basic: false, professional: true, enterprise: true },
        { name: '1-2 Hours/Month Support', basic: true, professional: false, enterprise: false },
        { name: '5-10 Hours/Month Support', basic: false, professional: true, enterprise: false },
        { name: 'Unlimited Support', basic: false, professional: false, enterprise: true },
        { name: 'Dedicated Manager', basic: false, professional: false, enterprise: true },
        { name: 'Strategic Consulting', basic: false, professional: false, enterprise: true },
        { name: 'Training Sessions', basic: false, professional: false, enterprise: true }
      ]
    },
    {
      category: 'Additional Features',
      items: [
        { name: 'Basic Training', basic: true, professional: true, enterprise: true },
        { name: 'Advanced Training', basic: false, professional: true, enterprise: true },
        { name: 'API Integration', basic: false, professional: true, enterprise: true },
        { name: 'Governance Setup', basic: false, professional: false, enterprise: true },
        { name: 'Project-based Options', basic: false, professional: false, enterprise: true }
      ]
    }
  ];

  const getPriceRange = (plan: any) => {
    if (billingCycle === 'monthly') {
      return `${plan.monthlyPriceMin} – ${plan.monthlyPriceMax}`;
    } else {
      return `${plan.yearlyPriceMin} – ${plan.yearlyPriceMax}`;
    }
  };

  const getSavings = (plan: any) => {
    if (billingCycle === 'yearly') {
      return plan.monthlyPriceMax * 12 - plan.yearlyPriceMax;
    }
    return 0;
  };

  return (
    <div className="min-h-screen">
      <SEOHead 
        title="Pricing - Power Platform Development & Support Plans"
        description="Choose the perfect Power Platform development and support plan for your business. All plans include our core features with flexible options to scale as you grow."
        keywords="Power Platform pricing, Dynamics 365 pricing, Power Apps development cost, Power Automate pricing, consulting rates"
        canonical="/pricing"
      />
      
      {/* Hero Section */}
      <section className="hero-gradient particle-bg relative overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="container-custom relative z-10 py-12">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">
               <span className="gradient-text">Simple Pricing</span>
            </h1>
            <p className="text-xl text-black/90 max-w-3xl mx-auto mb-8">
              Choose the perfect Power Platform development and support plan for your business. All plans include our core features with flexible options to scale as you grow.
            </p>
            <p className="text-lg text-black/80 max-w-2xl mx-auto mb-8">
              💡 Click on any plan card to select it, or use the "Get Started" button
            </p>
            
            {/* Billing Toggle */}
            <div className="flex items-center justify-center space-x-4 mb-8">
              <span className="text-black font-medium">Monthly</span>
              <button
                onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
                className={`relative inline-flex h-12 w-24 items-center rounded-full transition-colors ${
                  billingCycle === 'yearly' ? 'bg-primary-500' : 'bg-black/20'
                }`}
              >
                <span
                  className={`inline-block h-8 w-8 transform rounded-full bg-white transition-transform ${
                    billingCycle === 'yearly' ? 'translate-x-12' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className="text-black font-medium">Yearly</span>
              {billingCycle === 'yearly' && (
                <span className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                  Save 20%
                </span>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pricing Plans */}
      <section className="py-12 bg-white/95 backdrop-blur-xl">
        <div className="container-custom">
          <div className="grid md:grid-cols-3 gap-8">
            {plans.map((plan, index) => (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.8 }}
                className={`relative ${plan.popular ? 'scale-105' : ''}`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className="bg-gradient-to-r from-primary-500 to-accent-500 text-white px-6 py-2 rounded-full text-sm font-bold shadow-2xl">
                      Most Popular
                    </div>
                  </div>
                )}
                {selectedPlan === plan.id && (
                  <div className="absolute -top-4 right-4">
                    <div className="bg-green-500 text-white px-4 py-2 rounded-full text-sm font-bold shadow-2xl flex items-center">
                      <Check className="w-4 h-4 mr-1" />
                      Selected
                    </div>
                  </div>
                )}
                
                <div className={`feature-card h-full transition-all duration-300 cursor-pointer hover:scale-105 hover:shadow-2xl ${
                  plan.popular ? 'ring-4 ring-primary-500' : ''
                } ${
                  selectedPlan === plan.id ? 'ring-4 ring-green-500 shadow-2xl scale-105' : ''
                }`}
                onClick={() => setSelectedPlan(plan.id)}>
                  <div className="text-center mb-8">
                    <div className={`w-16 h-16 bg-gradient-to-br ${plan.color} rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-2xl`}>
                      <plan.icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                    <p className="text-gray-600 mb-6">{plan.description}</p>
                    
                                         <div className="mb-6">
                       <div className="text-4xl font-bold text-gray-900">
                         ${getPriceRange(plan)}
                         <span className="text-lg text-gray-500">/{billingCycle === 'monthly' ? 'mo' : 'year'}</span>
                       </div>
                       {getSavings(plan) > 0 && (
                         <div className="text-green-600 font-medium mt-2">
                           Save ${getSavings(plan)} per year
                         </div>
                       )}
                     </div>
                  </div>

                  <div className="space-y-4 mb-8">
                    <h4 className="font-bold text-gray-900 mb-4">What's Included:</h4>
                    {plan.features.map((feature, featureIndex) => (
                      <motion.div
                        key={feature}
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ delay: featureIndex * 0.05, duration: 0.5 }}
                        className="flex items-center space-x-3"
                      >
                        <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </motion.div>
                    ))}
                    
                    {plan.notIncluded.length > 0 && (
                      <>
                        <h4 className="font-bold text-gray-900 mb-4 mt-6">Not Included:</h4>
                        {plan.notIncluded.map((feature, featureIndex) => (
                          <motion.div
                            key={feature}
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ delay: (featureIndex + plan.features.length) * 0.05, duration: 0.5 }}
                            className="flex items-center space-x-3"
                          >
                            <X className="w-5 h-5 text-red-500 flex-shrink-0" />
                            <span className="text-gray-500">{feature}</span>
                          </motion.div>
                        ))}
                      </>
                    )}
                  </div>

                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent card click when clicking button
                      setSelectedPlan(plan.id);
                    }}
                    className={`w-full py-4 px-6 rounded-2xl font-bold transition-all duration-300 ${
                      selectedPlan === plan.id
                        ? 'bg-green-500 text-white shadow-2xl hover:bg-green-600'
                        : plan.popular
                        ? 'bg-gradient-to-r from-primary-500 to-accent-500 text-white shadow-2xl hover:shadow-primary-500/25'
                        : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                    }`}
                  >
                    {selectedPlan === plan.id ? 'Selected' : 'Get Started'}
                    <ArrowRight className="ml-2 w-5 h-5 inline" />
                  </motion.button>
                </div>
              </motion.div>
            ))}
          </div>
          
          {/* Selection Summary */}
          {selectedPlan && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="mt-12 bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-200 rounded-3xl p-8 shadow-xl"
            >
              <div className="text-center">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  🎉 You've Selected: {plans.find(p => p.id === selectedPlan)?.name}
                </h3>
                <p className="text-gray-600 mb-6">
                  Ready to get started? Contact us to discuss your requirements and begin your Power Platform journey.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={openContact}
                    className="bg-gradient-to-r from-primary-500 to-accent-500 text-white px-8 py-4 rounded-2xl font-bold shadow-2xl hover:shadow-primary-500/25 transition-all duration-300"
                  >
                    Contact Sales
                    <ArrowRight className="ml-2 w-5 h-5 inline" />
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setSelectedPlan(null)}
                    className="bg-gray-100 text-gray-700 px-8 py-4 rounded-2xl font-bold hover:bg-gray-200 transition-all duration-300"
                  >
                    Change Selection
                  </motion.button>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </section>

      {/* Contact Modal */}
      <AnimatePresence>
        {isContactOpen && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="absolute inset-0 bg-black/50" onClick={closeContact} />
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 10 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 10 }}
              transition={{ duration: 0.2 }}
              className="relative z-10 w-full max-w-xl bg-white rounded-2xl shadow-2xl overflow-hidden"
            >
              <div className="bg-gradient-to-r from-primary-600 to-accent-600 p-4 text-white">
                <h3 className="text-2xl font-bold">Send us a Message</h3>
                <p className="opacity-90">Tell us about your needs and our team will contact you shortly.</p>
              </div>
              <div className="p-4">
                {/* Inline require to avoid circular import issues */}
                <div className="text-sm">
                  {(() => {
                    const ContactForm = require('../components/ContactForm').default;
                    return (
                      <ContactForm onSuccess={closeContact} className="text-sm" />
                    );
                  })()}
                </div>
              </div>
              <button
                onClick={closeContact}
                className="absolute top-3 right-4 text-white/90 hover:text-white"
                aria-label="Close"
              >
                ✕
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Feature Comparison */}
      <section className="section-gradient py-12">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
               <span className="gradient-text">Feature Comparison</span>
            </h2>
            <p className="text-xl text-black/90 max-w-3xl mx-auto">
              Compare our plans side by side to find the perfect fit for your business needs.
            </p>
          </motion.div>

          <div className="overflow-x-auto">
            <table className="w-full bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="p-6 text-left font-bold text-gray-900">Features</th>
                  <th className="p-6 text-center font-bold text-gray-900">Basic</th>
                  <th className="p-6 text-center font-bold text-gray-900">Professional</th>
                  <th className="p-6 text-center font-bold text-gray-900">Enterprise</th>
                </tr>
              </thead>
              <tbody>
                {features.map((category, categoryIndex) => (
                  <React.Fragment key={category.category}>
                    <tr className="bg-gray-50">
                      <td colSpan={4} className="p-4 font-bold text-gray-900">
                        {category.category}
                      </td>
                    </tr>
                    {category.items.map((item, itemIndex) => (
                      <motion.tr
                        key={item.name}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: (categoryIndex * 5 + itemIndex) * 0.1, duration: 0.5 }}
                        className="border-b border-gray-100"
                      >
                        <td className="p-6 text-gray-700">{item.name}</td>
                        <td className="p-6 text-center">
                          {item.basic ? (
                            <Check className="w-6 h-6 text-green-500 mx-auto" />
                          ) : (
                            <X className="w-6 h-6 text-red-500 mx-auto" />
                          )}
                        </td>
                        <td className="p-6 text-center">
                          {item.professional ? (
                            <Check className="w-6 h-6 text-green-500 mx-auto" />
                          ) : (
                            <X className="w-6 h-6 text-red-500 mx-auto" />
                          )}
                        </td>
                        <td className="p-6 text-center">
                          {item.enterprise ? (
                            <Check className="w-6 h-6 text-green-500 mx-auto" />
                          ) : (
                            <X className="w-6 h-6 text-red-500 mx-auto" />
                          )}
                        </td>
                      </motion.tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Add-ons Section */}
      <section className="py-12 bg-white/95 backdrop-blur-xl">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
               <span className="gradient-text">Additional Services</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Enhance your Power Platform experience with our professional services and add-ons.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {addOns.map((addon, index) => (
              <motion.div
                key={addon.name}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.8 }}
                className="feature-card text-center"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-accent-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-2xl">
                  <addon.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{addon.name}</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">{addon.description}</p>
                <div className="text-2xl font-bold text-primary-600 mb-4">
                  ${addon.price}/month
                </div>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="btn-primary w-full"
                >
                  Add Service
                </motion.button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Enterprise Solutions */}
      <section className="section-gradient py-12">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                 <span className="gradient-text">Enterprise Power Platform Solutions</span>
              </h2>
              <p className="text-xl text-black/90 mb-8 leading-relaxed">
                For large organizations with complex requirements, we offer custom Power Platform enterprise solutions tailored to your specific needs.
              </p>
              
              <div className="space-y-6">
                {[
                  {
                    icon: Building2,
                    title: "Custom Power Platform Development",
                    description: "Tailored Power Platform solutions built specifically for your business processes and requirements."
                  },
                  {
                    icon: Users,
                    title: "Dedicated Power Platform Team",
                    description: "Your own team of Power Platform experts working exclusively on your projects."
                  },
                  {
                    icon: Shield,
                    title: "Enterprise Security & Governance",
                    description: "Advanced security features, governance setup, and compliance solutions for enterprise environments."
                  }
                ].map((item, index) => (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1, duration: 0.5 }}
                    className="flex items-start space-x-4"
                  >
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-6 h-6 text-black" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold mb-4 gradient-text">{item.title}</h3>
                      <p className="text-black/80 leading-relaxed">{item.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.8 }}
                className="mt-8"
              >
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="btn-accent text-lg px-8 py-4"
                >
                  Contact Enterprise Sales
                  <ArrowRight className="ml-2 w-5 h-5" />
                </motion.button>
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="floating-animation">
                <div className="bg-white/20 backdrop-blur-xl rounded-3xl p-8 border border-white/30 shadow-2xl">
                  <div className="text-center text-black">
                    <Crown className="w-16 h-16 mx-auto mb-6 text-gold-400" />
                    <h3 className="text-2xl font-bold mb-4 gradient-text">Enterprise Power Platform Package</h3>
                    <div className="text-4xl font-bold mb-4 text-blue">Custom Pricing</div>
                    <p className="text-lg leading-relaxed mb-6">
                      Contact us for a personalized quote based on your specific Power Platform requirements and scale.
                    </p>
                    <div className="space-y-3 text-left">
                      <div className="flex items-center space-x-3">
                        <Check className="w-5 h-5 text-green-400" />
                        <span>Full Power Platform Suite</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <Check className="w-5 h-5 text-green-400" />
                        <span>Custom Power Apps Development</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <Check className="w-5 h-5 text-green-400" />
                        <span>Dedicated Support Team</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <Check className="w-5 h-5 text-green-400" />
                        <span>Strategic Consulting</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 bg-white/95 backdrop-blur-xl">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
               <span className="gradient-text">Frequently Asked Questions</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Get answers to common questions about our pricing and services.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                question: "Can I change my plan later?",
                answer: "Yes, you can upgrade or downgrade your plan at any time. Changes will be prorated based on your billing cycle."
              },
              {
                question: "Is there a setup fee?",
                answer: "No setup fees for our standard plans. Custom enterprise solutions may have implementation costs."
              },
              {
                question: "What payment methods do you accept?",
                answer: "We accept all major credit cards, PayPal, and bank transfers for annual plans."
              },
              {
                question: "Do you offer refunds?",
                answer: "We offer a 30-day money-back guarantee for all new subscriptions."
              },
              {
                question: "Is training included?",
                answer: "Basic training is included with Professional and Enterprise plans. Additional training is available as an add-on."
              },
              {
                question: "Can I cancel anytime?",
                answer: "Yes, you can cancel your subscription at any time with no cancellation fees."
              }
            ].map((faq, index) => (
              <motion.div
                key={faq.question}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.8 }}
                className="feature-card"
              >
                <h3 className="text-xl font-bold text-gray-900 mb-4">{faq.question}</h3>
                <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 bg-gradient-to-r from-primary-600 to-accent-600">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-black 6">
            Ready to Get Started?
            </h2>
            <p className="text-xl text-black/90 mb-8 max-w-2xl mx-auto">
              Choose the perfect plan for your business and start transforming your operations with Power Platform solutions.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="btn-accent text-lg px-8 py-4"
              >
                Start Free Trial
                <ArrowRight className="ml-2 w-5 h-5" />
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="btn-outline text-lg px-8 py-4"
              >
                Contact Sales
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Pricing; 